import type { CityConfig } from "../city-template/types";

export const nixaMOConfig: CityConfig = {
  city: "Nixa",
  state: "Missouri",
  stateAbbr: "MO",
  slug: "nixa-mo",
  route: "/cities/nixa-mo",
  title: "Emergency Passport Nixa, MO — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Nixa, MO — as fast as 24 hours. Serving Christian County and the Springfield metro. A+ BBB rated registered State Dept. courier serving southwest Missouri. No office visit required.",
  h1: "Emergency & Expedited Passport Services in Nixa, MO",
  heroSubtitle: "Christian County business trip to Dublin with one week's notice? Southwest Missouri family vacation to Cancun and your passport expired? Ozark Mountain area traveler heading abroad needs fast documents? We help Nixa residents get their passports fast — as fast as 24 hours. A+ BBB rated registered State Dept. courier. Best price guarantee. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Nixa%20Missouri%20Christian%20County%20Ozark%20foothills%20landscape%20suburban%20community%20rolling%20green%20hills%20James%20River%20valley%20modern%20subdivisions%20historic%20downtown%20Main%20Street%20area%20southwest%20Missouri%20spring%20season%20blooming%20dogwoods%20professional%20photography&width=1800&height=700&seq=nixa-mo-hero-2026&orientation=landscape",
  heroImageAlt: "Nixa Missouri Christian County Ozark foothills suburban landscape at springtime",
  geoPosition: "37.0434;-93.2949",
  geoPlacename: "Nixa, Missouri",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "101 Ida B. Wells Drive, 9th Floor, Chicago, IL 60604",
    distance: "Located in downtown Chicago — approximately 530 miles northeast of Nixa via I-44/I-55",
  },
  nearbyCitiesZips: [
    { name: "Nixa", zips: "65714" },
    { name: "Ozark", zips: "65721" },
    { name: "Springfield", zips: "65801, 65802, 65803, 65804, 65806, 65807" },
    { name: "Clever", zips: "65631" },
    { name: "Sparta", zips: "65753" },
    { name: "Highlandville", zips: "65669" },
    { name: "Rogersville", zips: "65742" },
    { name: "Republic", zips: "65738" },
    { name: "Battlefield", zips: "65619" },
    { name: "Billings", zips: "65610" },
    { name: "Marionville", zips: "65705" },
    { name: "Branson", zips: "65616" },
    { name: "Hollister", zips: "65672" },
    { name: "Marshfield", zips: "65706" },
    { name: "Mount Vernon", zips: "65712" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent southwest Missouri departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-map-pin-2-line", title: "Christian County Hub", desc: "Serving Nixa, Ozark, Clever, and the greater Springfield metro area" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Springfield, MO", route: "/cities/springfield-mo" },
    { name: "Fast passport renewal in Joplin, MO", route: "/cities/joplin" },
    { name: "Emergency passport help in Branson, MO", route: "#" },
    { name: "Urgent passport services in Kansas City, MO", route: "/cities/kansas-city" },
    { name: "Same-day passport help in Chicago, IL", route: "/cities/chicago" },
    { name: "Passport courier services in St. Louis, MO", route: "/cities/st-louis" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Nixa remotely via secure courier. We do not have a walk-in office in Nixa — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Christian County and southwest Missouri.",
  fpcOfficeAddress: "525 N Tryon St., Charlotte, NC 28202",
  fpcOfficeDistance: "Serving all of Christian County and the Springfield metro including Stone County and Taney County",
  serviceAreaIntro: "From the growing subdivisions and excellent school district of Nixa to the charming downtown of Ozark along the Finley River, from the bustling retail corridors of southern Springfield to the rural communities of Clever and Sparta, from the entertainment destination of Branson to the rolling Ozark foothills of Christian County — Fast Passport Center serves the entire Springfield metro and southwest Missouri region. We regularly help travelers from Nixa, Ozark, and the surrounding communities get their passports fast without the 9-hour drive to Chicago.",
  nearbyCitiesIntro: "We serve all Nixa neighborhoods and the surrounding Christian County area including Ozark, Clever, Sparta, Highlandville, Rogersville, and Republic. If you are anywhere in Christian County or the greater Springfield metro, we have you covered.",
  howItWorksIntro: "Most Nixa travelers complete everything online and ship documents overnight. No need to drive to Chicago or wait in line at the regional agency.",
  mistakesIntro: "Nixa travelers often face a 9-hour drive to the nearest regional passport agency in Chicago. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Nixa and Christian County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Nixa? Here are the most common ones we hear from southwest Missouri travelers.",
  statePageRoute: "/states/missouri",
  statePageName: "Missouri",
  dmvWebsite: "https://dor.mo.gov/drivers/",
  dmvLabel: "Missouri DOR — Driver License Bureau",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Nixa travelers with immediate departure needs — as fast as 24 hours for urgent international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-bank-card-line",
      title: "Passport Card",
      description: "Wallet-sized U.S. Passport Card for land and sea travel to Canada, Mexico, Bermuda, and the Caribbean.",
      bullets: ["REAL ID compliant alternative", "Fits in your wallet", "Land/sea border crossing approved"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-card",
    },
    {
      icon: "ri-error-warning-line",
      title: "Damaged Passport",
      description: "Replacement for water-damaged, torn, or otherwise mutilated passports that are no longer valid for travel.",
      bullets: ["Damage assessment guidance", "DS-11 replacement process", "Supporting documentation help"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=damaged-passport",
    },
    {
      icon: "ri-file-copy-line",
      title: "Second Passport",
      description: "Obtain a second valid U.S. passport for frequent international travelers who need overlapping visas.",
      bullets: ["For frequent business travelers", "Overlapping visa support", "Expedited processing available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
    { name: "Passport Card", description: "Wallet-sized U.S. Passport Card for land and sea travel to Canada, Mexico, Bermuda, and the Caribbean." },
    { name: "Damaged Passport", description: "Replacement for water-damaged, torn, or mutilated passports requiring DS-11 replacement." },
    { name: "Second Passport", description: "Second valid U.S. passport for frequent international travelers with overlapping visa needs." },
  ],
  testimonials: [
    {
      name: "Lisa H.",
      location: "Nixa, MO",
      rating: 5,
      text: "Our family cruise to the Bahamas was in 4 days when I realized the kids' passports were expired. Fast Passport Center got all three in 36 hours. I never left Nixa. Christian County's best-kept secret!",
      service: "Child Passport",
    },
    {
      name: "Greg W.",
      location: "Ozark, MO",
      rating: 5,
      text: "Business trip to London with 5 days notice. My passport was expired. Fast Passport Center had my renewal completed in 30 hours. The dedicated agent was phenomenal — daily updates, no stress.",
      service: "Passport Renewal",
    },
    {
      name: "Carrie M.",
      location: "Nixa, MO",
      rating: 5,
      text: "Medical mission trip to Guatemala and my passport was lost. Fast Passport Center got my replacement in 28 hours. From the Ozarks to Central America in 3 days. Absolutely incredible service!",
      service: "Lost or Stolen Passport",
    },
  ],
  facilities: [
    {
      id: "nixa-1",
      name: "Nixa Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1116 W Mount Vernon St",
      city: "Nixa",
      state: "MO",
      zip: "65714",
      phone: "(417) 725-6111",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Downtown Nixa",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Nixa facility. Appointments strongly recommended. Call ahead to confirm passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "nixa-2",
      name: "Christian County Recorder",
      type: "County Recorder Passport Acceptance",
      address: "100 W Church St",
      city: "Ozark",
      state: "MO",
      zip: "65721",
      phone: "(417) 581-6372",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "6 miles south of Nixa",
      website: "https://www.christiancounty.mo.gov",
      websiteLabel: "Christian County Recorder",
      notes: "Accepts passport applications at the county seat. Good alternative to post office. Appointment recommended.",
      icon: "ri-government-line",
    },
    {
      id: "nixa-3",
      name: "Springfield Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "500 W Chestnut Expy",
      city: "Springfield",
      state: "MO",
      zip: "65802",
      phone: "(417) 864-0111",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "12 miles north of Nixa",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Springfield and the greater metro area. Full passport services available. Walk-ins accepted.",
      icon: "ri-mail-line",
    },
    {
      id: "nixa-4",
      name: "Ozark Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1450 W Jackson St",
      city: "Ozark",
      state: "MO",
      zip: "65721",
      phone: "(417) 581-6111",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "6 miles south of Nixa",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Ozark and southern Christian County. Full passport services available.",
      icon: "ri-mail-line",
    },
    {
      id: "nixa-5",
      name: "Republic Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1300 N Main Ave",
      city: "Republic",
      state: "MO",
      zip: "65738",
      phone: "(417) 732-6111",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "15 miles west of Nixa",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Republic and western Greene County. Full passport services available.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 9 hour drive to Chicago", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Nixa photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Christian County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Nixa applicants show up with incorrect payment and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Chicago regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many southwest Missouri families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Nixa travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Nixa." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Nixa address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Nixa before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Nixa for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the 9-hour drive to Chicago." },
    { q: "How fast can I renew my passport in Nixa?", a: "Fast Passport Center offers fast passport renewal in Nixa in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What is the closest passport agency to Nixa?", a: "The nearest regional passport agency to Nixa is the Chicago Passport Agency, approximately 530 miles northeast via I-44/I-55. For urgent processing without the long drive, Fast Passport Center's registered courier service achieves 24–48 hour turnaround from Nixa without any in-person agency visit required." },
    { q: "Can I get a same-day passport appointment near Nixa?", a: "Same-day passport appointments are only available at the Chicago Regional Passport Agency — approximately 9 hours from Nixa — for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Christian County and southwest Missouri residents." },
    { q: "Do you serve the entire Christian County area?", a: "Absolutely. Fast Passport Center serves all of Christian County and the Springfield metro including Nixa, Ozark, Clever, Sparta, Rogersville, Republic, and surrounding areas. Our courier service works seamlessly from any address in Christian County or Greene County." },
    { q: "Can I expedite a child passport in Nixa?", a: "Yes. Fast Passport Center expedites child passports in Nixa for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk Christian County families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Nixa with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "Do I need to drive to the Chicago Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
  ],
  photoLocations: [
    { name: "Walmart Supercenter — Nixa", address: "125 N Massey Blvd, Nixa, MO 65714", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Walgreens — Springfield (Republic Rd)", address: "2650 W Republic Rd, Springfield, MO 65807", hours: "Mon–Sun: 7AM–11PM" },
    { name: "CVS Pharmacy — Ozark", address: "2000 W Marler Ln, Ozark, MO 65721", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart Supercenter — Ozark", address: "2000 W Marler Ln, Ozark, MO 65721", hours: "Mon–Sun: 6AM–11PM" },
  ],
  dmvLocations: [
    {
      name: "Missouri DOR — Nixa License Office",
      address: "1862 N Commerce Dr, Nixa, MO 65714",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Driver license and REAL ID services in Nixa. Appointment recommended.",
      featured: true,
    },
    {
      name: "Missouri DOR — Springfield South License Office",
      address: "3755 S Campbell Ave, Springfield, MO 65807",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Serves south Springfield. REAL ID available. Appointment recommended.",
      featured: false,
    },
    {
      name: "Missouri DOR — Ozark License Office",
      address: "1450 W Jackson St, Ozark, MO 65721",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Serves Ozark and Christian County. REAL ID available.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "SGF",
      name: "Springfield-Branson National Airport",
      distance: "25 miles north of Nixa",
      address: "2300 N Airport Blvd, Springfield, MO 65802",
      note: "Regional airport with direct flights to major hubs. About 35 minutes from Nixa via US-65.",
      flightUrl: "https://www.flyspringfield.com/",
      flightLabel: "SGF Airport",
      featured: true,
    },
    {
      code: "JLN",
      name: "Joplin Regional Airport",
      distance: "80 miles west of Nixa",
      address: "7331 N Hwy 43, Joplin, MO 64801",
      note: "Regional airport serving Joplin and southwest Missouri. About 1 hour 20 minutes from Nixa.",
      flightUrl: "https://www.joplinmo.org/airport",
      flightLabel: "JLN Airport",
      featured: false,
    },
    {
      code: "XNA",
      name: "Northwest Arkansas National Airport",
      distance: "90 miles southwest of Nixa",
      address: "1 Airport Blvd, Bentonville, AR 72712",
      note: "Regional airport serving northwest Arkansas. About 1 hour 30 minutes from Nixa via I-49.",
      flightUrl: "https://www.flyxna.com/",
      flightLabel: "XNA Airport",
      featured: false,
    },
  ],
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { icon: "ri-file-text-line", label: "Birth & Death Certificates", desc: "Missouri Department of Health and Senior Services Bureau of Vital Records in Jefferson City — order certified birth and death certificates for Christian County residents online, by mail, or in person.", url: "https://health.mo.gov/data/vitalrecords/" },
    { icon: "ri-heart-line", label: "Marriage Records", desc: "Christian County Recorder of Deeds — certified marriage licenses for name change passport applications and legal documentation.", url: "https://www.christiancounty.mo.gov/" },
    { icon: "ri-scales-line", label: "Divorce Decrees", desc: "Christian County Circuit Clerk — certified divorce decrees supporting passport name change or correction.", url: "https://www.courts.mo.gov/" },
    { icon: "ri-global-line", label: "VitalChek — Nationwide Records", desc: "If your record was issued outside Missouri, order certified copies online through VitalChek for delivery in 5–10 business days.", url: "https://www.vitalchek.com/" },
  ],
};